import React, { useCallback, useEffect, useState } from 'react';
import api from '../utils/api';
import './CampusLogin.css';

const DOC_LABELS = {
  doc_first_aid: 'First Aid Certificate',
  doc_pic: 'Police Information Check',
  doc_immunization: 'Immunization Record',
  doc_resume: 'Resume',
};
const OUTCOME_LABELS = {
  matched: 'Matched',
  unmatched: 'No match',
  ambiguous: 'Needs review',
  failed: 'Check failed',
};
const outcomeTone = outcome => outcome === 'matched' ? 'ok' : outcome === 'failed' ? 'bad' : 'warn';
const displayField = key => DOC_LABELS[key] || String(key).replace(/_/g, ' ').replace(/^doc /, '').replace(/\b\w/g, c => c.toUpperCase());
const fmtTime = value => value ? new Date(value).toLocaleString() : '—';

export default function CampusLoginStudentSync({ studentId, archived = false, onRequiredDocs, onApplied }) {
  const [record, setRecord] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [candidateId, setCandidateId] = useState('');
  const [manual, setManual] = useState(false);
  const [mailListId, setMailListId] = useState('');
  const [contactId, setContactId] = useState('');
  const [reason, setReason] = useState('');

  const load = useCallback(async () => {
    try {
      setError('');
      const res = await api.get(`/campuslogin/students/${studentId}`);
      setRecord(res.data);
      if (Array.isArray(res.data.requiredDocs) && onRequiredDocs) {
        onRequiredDocs(res.data.requiredDocs.map(key => ({ key, label: displayField(key) })));
      }
    } catch (err) {
      setError(err.response?.data?.error || 'CampusLogin sync history could not be loaded.');
    }
  }, [studentId, onRequiredDocs]);

  useEffect(() => { load(); }, [load]);

  const runPreview = async () => {
    setBusy('preview');
    setError('');
    setPreview(null);
    try {
      const res = await api.post(`/campuslogin/students/${studentId}/preview`);
      setPreview(res.data);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not preview CampusLogin sync.');
    } finally { setBusy(''); }
  };

  const saveMapping = async e => {
    e.preventDefault();
    const candidate = !manual && preview?.candidates?.find(c => `${c.mailListId}:${c.contactId}` === candidateId);
    const selectedMailListId = manual ? Number(mailListId) : candidate?.mailListId;
    const selectedContactId = manual ? Number(contactId) : candidate?.contactId;
    if (!Number.isFinite(selectedMailListId) || !Number.isFinite(selectedContactId) || !reason.trim()) return;
    setBusy('mapping');
    setError('');
    try {
      await api.post(`/campuslogin/students/${studentId}/mapping`, {
        mailListId: selectedMailListId,
        contactId: selectedContactId,
        confirmed: true,
        reason: reason.trim(),
      });
      setReason('');
      setPreview(null);
      setCandidateId('');
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Mapping could not be saved.');
    } finally { setBusy(''); }
  };

  const applyPreview = async () => {
    if (!preview?.previewToken || preview.outcome !== 'matched') return;
    setBusy('apply');
    setError('');
    try {
      const res = await api.post(`/campuslogin/students/${studentId}/apply`, { previewToken: preview.previewToken });
      setPreview({ ...res.data, previewToken: null });
      if (res.data.student && onApplied) onApplied(res.data.student);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'CampusLogin changes were not applied.');
    } finally { setBusy(''); }
  };

  const lastCheck = record?.lastCheck;
  const evidence = lastCheck?.evidence || [];
  const canMap = !archived && !!preview;
  const canApply = !archived && preview?.outcome === 'matched' && !!preview.previewToken;
  const summary = preview?.error || (preview?.outcome === 'matched'
    ? `${preview.changes?.length || 0} prerequisite field${preview.changes?.length === 1 ? '' : 's'} ready to sync.`
    : preview?.outcome === 'ambiguous'
      ? 'More than one possible person was found. Verify externally before assigning a mapping.'
      : preview?.outcome === 'unmatched'
        ? 'No person was matched. Verify the correct record externally before entering a manual mapping.'
        : preview?.outcome === 'failed' ? 'The check did not complete. No changes were applied.' : '');

  return (
    <section className="cl-panel" aria-label="CampusLogin prerequisite sync">
      <div className="cl-panel-inner">
        <div className="cl-title-row">
          <div>
            <div className="cl-eyebrow">CampusLogin · prerequisite sync</div>
            <h3 className="cl-title">Document presence check</h3>
            <p className="cl-copy">Checks whether required documents are present in the selected CampusLogin record. It does not verify document contents. No local file is copied and no email is sent.</p>
          </div>
          <span className={`cl-tag ${lastCheck ? outcomeTone(lastCheck.outcome) : ''}`}>
            {lastCheck ? OUTCOME_LABELS[lastCheck.outcome] || lastCheck.outcome : 'Not checked'}
          </span>
        </div>

        {error && <div className="cl-alert error" role="alert">{error}</div>}
        <div className="cl-status">
          <span><strong>Last check:</strong> {lastCheck ? fmtTime(lastCheck.checkedAt) : 'No check yet'}</span>
          {lastCheck?.lastSuccessAt && <span><strong>Last successful presence check:</strong> {fmtTime(lastCheck.lastSuccessAt)}</span>}
          {record?.mapping && <span><strong>Confirmed mapping:</strong> list {record.mapping.mailListId} · contact {record.mapping.contactId}</span>}
        </div>

        {evidence.length > 0 && (
          <div className="cl-evidence" aria-label="Retained successful evidence">
            {evidence.map((item, index) => (
              <div className="cl-evidence-row" key={`${item.field}-${item.documentId}-${index}`}>
                <span>{displayField(item.field)}</span>
                <span className="cl-evidence-id">Document {item.documentId} · {item.category}{item.foundAt ? ` · Found ${fmtTime(item.foundAt)}` : ''}</span>
              </div>
            ))}
            <div className="cl-note">Previously found in CampusLogin; retained evidence does not guarantee the document is still present. No contents were verified and no local file was created.</div>
          </div>
        )}
        {lastCheck?.error && <div className="cl-alert error">{lastCheck.error}</div>}

        {!archived ? (
          <div className="cl-actions">
            <button className="btn btn-secondary btn-sm" onClick={runPreview} disabled={!!busy}>
              {busy === 'preview' ? 'Checking…' : 'Preview check'}
            </button>
            {canApply && (
              <button className="btn btn-primary btn-sm" onClick={applyPreview} disabled={!!busy}>
                {busy === 'apply' ? 'Applying…' : 'Apply previewed changes'}
              </button>
            )}
          </div>
        ) : (
          <div className="cl-note">Archived student — CampusLogin controls are read-only. Last check history remains available.</div>
        )}

        {preview && (
          <div className={`cl-alert${preview.outcome === 'failed' ? ' error' : ''}`}>
            <strong>{OUTCOME_LABELS[preview.outcome] || preview.outcome}.</strong> {summary}
            {preview.changes?.length > 0 && <div className="cl-note">Changes: {preview.changes.map(displayField).join(', ')}</div>}
          </div>
        )}

        {preview?.evidence?.length > 0 && (
          <div className="cl-evidence">
            {preview.evidence.map((item, i) => (
              <div className="cl-evidence-row" key={`${item.field}-${item.documentId}-${i}`}>
                <span>{displayField(item.field)}</span>
                <span className="cl-evidence-id">Document {item.documentId} · {item.category}</span>
              </div>
            ))}
          </div>
        )}

        {canMap && (
          <form onSubmit={saveMapping}>
            <hr className="cl-divider" />
            <div className="cl-title" style={{ fontSize: 13 }}>Confirm the person before mapping</div>
            <p className="cl-copy">Candidate names and email addresses are suggestions only. Verify the same person externally before confirming. Your reason is recorded with the mapping.</p>
            {(preview.candidates || []).map(candidate => (
              <label className="cl-candidate" key={`${candidate.mailListId}-${candidate.contactId}`}>
                <input type="radio" name={`candidate-${studentId}`} value={`${candidate.mailListId}:${candidate.contactId}`}
                  checked={!manual && candidateId === `${candidate.mailListId}:${candidate.contactId}`}
                  onChange={() => { setCandidateId(`${candidate.mailListId}:${candidate.contactId}`); setManual(false); setMailListId(String(candidate.mailListId)); setContactId(String(candidate.contactId)); }} />
                <span className="cl-candidate-main">
                  <strong>{candidate.firstName} {candidate.lastName}</strong>
                  <span className="cl-candidate-sub">{candidate.email || 'No email'} · List {candidate.mailListId} · Contact {candidate.contactId} · {candidate.source}</span>
                </span>
              </label>
            ))}
            <label className="cl-candidate">
              <input type="radio" name={`candidate-${studentId}`} checked={manual} onChange={() => setManual(true)} />
              <span className="cl-candidate-main"><strong>Enter verified IDs manually</strong><span className="cl-candidate-sub">Use only after verifying the same person in CampusLogin.</span></span>
            </label>
            {manual && (
              <div className="cl-form">
                <label className="cl-field">Mailing list ID<input value={mailListId} onChange={e => setMailListId(e.target.value)} required /></label>
                <label className="cl-field">Contact ID<input value={contactId} onChange={e => setContactId(e.target.value)} required /></label>
              </div>
            )}
            <div className="cl-form">
              <label className="cl-field full">Verification reason<textarea value={reason} onChange={e => setReason(e.target.value)} required minLength={5} maxLength={500} placeholder="How did you verify this is the same person?" /></label>
            </div>
            <div className="cl-actions">
              <button className="btn btn-primary btn-sm" type="submit" disabled={!!busy || !reason.trim() || (!manual && !candidateId) || (manual && (!mailListId.trim() || !contactId.trim()))}>
                {busy === 'mapping' ? 'Saving confirmation…' : 'Confirm mapping'}
              </button>
            </div>
          </form>
        )}
      </div>
    </section>
  );
}
